import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, GraduationCap, UserSquare, User } from "lucide-react";

const KIND = { student: { label: "Studente 3° anno", icon: GraduationCap }, user: { label: "Membro Classroom", icon: UserSquare }, free: { label: "Nome libero", icon: User } };
const err = (e) => toast.error(e.response?.data?.detail || "Errore");

export function PeopleTab({ slots, people }) {
  const qc = useQueryClient();
  const [pick, setPick] = useState("");
  const [freeName, setFreeName] = useState("");
  const { data: candidates = [] } = useQuery({ queryKey: ["vig-candidates"], queryFn: async () => (await api.get("/vigilanza/candidates")).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["vig-people"] }); qc.invalidateQueries({ queryKey: ["vig-candidates"] }); qc.invalidateQueries({ queryKey: ["vig-shifts"] }); };

  const addMut = useMutation({
    mutationFn: async (body) => (await api.post("/vigilanza/people", body)).data,
    onSuccess: () => { toast.success("Persona aggiunta"); setPick(""); setFreeName(""); refresh(); }, onError: err,
  });
  const updMut = useMutation({
    mutationFn: async ({ id, ...body }) => (await api.patch(`/vigilanza/people/${id}`, body)).data, onSuccess: refresh, onError: err,
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/vigilanza/people/${id}`)).data,
    onSuccess: () => { toast.success("Persona rimossa (e i suoi turni)"); refresh(); }, onError: err,
  });

  const addCandidate = () => { const c = candidates.find((x) => `${x.kind}:${x.ref_id}` === pick); if (c) addMut.mutate({ kind: c.kind, ref_id: c.ref_id }); };
  const toggleSlot = (p, sid) => {
    const cur = p.slot_ids?.length ? p.slot_ids : slots.map((s) => s.id);
    const next = cur.includes(sid) ? cur.filter((x) => x !== sid) : [...cur, sid];
    updMut.mutate({ id: p.id, slot_ids: next.length === slots.length ? [] : next });
  };

  return (
    <div className="space-y-5 mt-4">
      <Card className="p-4 border border-stone-200 shadow-none rounded-xl grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="text-sm font-medium text-stone-800">Studente del 3° anno o membro di Classroom</div>
          <div className="flex gap-2">
            <select value={pick} onChange={(e) => setPick(e.target.value)} data-testid="vig-candidate-select" className="h-9 flex-1 min-w-0 rounded-md border border-stone-200 bg-white px-2 text-sm">
              <option value="">Scegli…</option>
              {candidates.map((c) => <option key={`${c.kind}:${c.ref_id}`} value={`${c.kind}:${c.ref_id}`}>{`${c.name} — ${c.detail}`}</option>)}
            </select>
            <Button onClick={addCandidate} disabled={!pick} data-testid="vig-add-candidate" className="bg-[#1C352D] hover:bg-[#2a4a40]"><Plus size={14}/></Button>
          </div>
        </div>
        <div className="space-y-2">
          <div className="text-sm font-medium text-stone-800">Nome libero (anche coppie, es. "Giuseppe e Antonietta")</div>
          <div className="flex gap-2">
            <Input value={freeName} onChange={(e) => setFreeName(e.target.value)} placeholder="Nome e cognome" data-testid="vig-free-name"/>
            <Button onClick={() => addMut.mutate({ kind: "free", name: freeName })} disabled={!freeName.trim()} data-testid="vig-add-free" className="bg-[#1C352D] hover:bg-[#2a4a40]"><Plus size={14}/></Button>
          </div>
        </div>
      </Card>

      {people.length === 0 && <div className="text-sm text-stone-400 text-center py-10">Nessuna persona inserita.</div>}
      <div className="grid gap-2">
        {people.map((p) => <PersonRow key={p.id} p={p} slots={slots} onToggleSlot={toggleSlot} onActive={(v) => updMut.mutate({ id: p.id, active: v })}
          onRename={(name) => updMut.mutate({ id: p.id, name })} onDelete={() => window.confirm(`Rimuovere ${p.name}? Verranno tolti anche i suoi turni.`) && delMut.mutate(p.id)}/>)}
      </div>
    </div>
  );
}

function PersonRow({ p, slots, onToggleSlot, onActive, onRename, onDelete }) {
  const K = KIND[p.kind] || KIND.free;
  const [name, setName] = useState(p.name);
  return (
    <Card className={`p-3 border border-stone-200 shadow-none rounded-xl flex flex-wrap items-center gap-3 ${p.active ? "" : "opacity-60"}`} data-testid={`vig-person-row-${p.id}`}>
      <K.icon size={16} className="text-stone-500"/>
      <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== p.name && onRename(name)} className="h-8 w-56" data-testid={`vig-person-name-${p.id}`}/>
      <span className="text-[11px] uppercase tracking-wider text-stone-500">{K.label}</span>
      <div className="flex gap-1.5 flex-wrap">
        {slots.map((s) => {
          const on = !p.slot_ids?.length || p.slot_ids.includes(s.id);
          return (
            <button key={s.id} onClick={() => onToggleSlot(p, s.id)} data-testid={`vig-person-slot-${p.id}-${s.id}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white text-stone-500 border-stone-200"}`}>{s.name}</button>
          );
        })}
      </div>
      <div className="ml-auto flex items-center gap-3">
        <label className="flex items-center gap-2 text-xs text-stone-600"><Switch checked={p.active} onCheckedChange={onActive} data-testid={`vig-person-active-${p.id}`}/> Attivo</label>
        <Button variant="ghost" size="icon" onClick={onDelete} data-testid={`vig-person-del-${p.id}`}><Trash2 size={14} className="text-red-600"/></Button>
      </div>
    </Card>
  );
}
